// #47 Candidate hero: the head of the Candidate profile on a card — the 72 px candidate tile (a rounded square, never
// a person's circle, DC-6), the name, the Candidate ID as an IdPill (copy on click, DC-29), the profile pills
// (*Active / Archived / Merged* · *Internal / External* · *Talent community*), the current role ("<title> at <employer>",
// his image), a current-state line, facts with their icons, and the actions on the right. No ring and no score: nothing
// the record does not know (DC-11, DC-33).

import { useId, type ReactNode } from "react";
import { IdPill } from "../core/Pill";
import { Icon, type IconName } from "../core/icons";
import { Avatar } from "../core/Person";
import { cx } from "../lib/cx";
import type { Hs } from "../lib/hs";

/** A fact under the name: an icon and its words or data (a requisition, a date, a source). */
export interface HeroFact {
  icon: IconName;
  text: ReactNode;
}

export interface CandidateHeroProps {
  /** The candidate's name (the h-level heading and the tile's initials). */
  name: string;
  /** The Candidate ID, e.g. CAN-2026-000123 (mono, copy on click). */
  candidateId: string;
  /** The tile's tint (a --t-* token var); default from the id. */
  tint?: `var(--${string})` | undefined;
  /** The profile pills: Active / Archived / Merged, Internal / External, Talent community. */
  pills?: ReactNode | undefined;
  /** The current role under the name: "<title> at <employer>" (a filled template). */
  headline?: ReactNode | undefined;
  /** One line under the name: where the candidate stands now. */
  now?: ReactNode | undefined;
  facts?: readonly HeroFact[] | undefined;
  /** Right-aligned actions (Buttons, a MenuButton). */
  actions?: ReactNode | undefined;
  /** Under the hero, full width (a banner, a stamp). */
  foot?: ReactNode | undefined;
  /** The name's heading level (default 2). */
  level?: 1 | 2 | 3 | undefined;
  /** false: the seat may not see the profile; nothing renders. */
  can?: boolean | undefined;
  /** Capture hotspot of the IdPill. */
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function CandidateHero({ name, candidateId, tint, pills, headline, now, facts, actions, foot, level = 2, can, hs, className }: CandidateHeroProps) {
  const nameId = useId();
  if (can === false) return null;
  const Heading = `h${level}` as const;
  return (
    <section className={cx("mc-card", "mc-hero-card", className)} aria-labelledby={nameId}>
      <div className="mc-hero">
        <Avatar name={name} hueKey={candidateId} candidate tint={tint} size={72} decorative />
        <div className="mc-hero-x">
          <div className="mc-hero-n">
            <Heading className="mc-hero-name" id={nameId}>{name}</Heading>
            <IdPill value={candidateId} hs={hs} />
            {pills ?? null}
          </div>
          {headline ? <div className="mc-hero-now">{headline}</div> : null}
          {now ? <div className="mc-hero-now">{now}</div> : null}
          {facts && facts.length > 0 ? (
            <ul className="mc-hero-l">
              {facts.map((fact, index) => (
                <li key={index}><Icon name={fact.icon} size={16} tone="muted" /><span>{fact.text}</span></li>
              ))}
            </ul>
          ) : null}
        </div>
        {actions ? <div className="mc-hero-a">{actions}</div> : null}
      </div>
      {foot ? <div className="mc-hero-f">{foot}</div> : null}
    </section>
  );
}
